import { useState, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Volume2, ChevronRight, ChevronLeft } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { FlexAlignCenter, FlexColumn } from '../shared/Flex';
import { DateTimeInput } from '../shared/DateTimeInput';
import { Text } from '../ui-elements/Main/Text';
import { SpanLink } from '../ui-elements/Main/SpanLink';
import { Checkbox } from '../ui-elements/Main/Checkbox';
import { colors } from '../ui-elements/Main/MainTheme';
import { UserInfoContext } from '../user/user-info';
import { pauseNotifications, resumeNotifications } from '@/redux/app/actions/user';
import { destructureDate, formatDateForDateTimeInput } from '../../../helpers/app/time';
import {
  snoozeUntilSelector,
  snoozeMuteChatSelector,
  snoozeMuteCallsSelector,
  userAudioSelector,
} from '@/redux/app/selectors/user';

const getMinimumCustomSnoozeTime = () => {
  const minTime = new Date(Date.now() + 60 * 1000);
  if (minTime.getSeconds() > 0 || minTime.getMilliseconds() > 0) {
    minTime.setMinutes(minTime.getMinutes() + 1);
  }
  minTime.setSeconds(0, 0);
  return minTime;
};

const getSnoozeUntil = (option: string): string => {
  const now = new Date();
  switch (option) {
    case '30min':
      return new Date(now.getTime() + 30 * 60 * 1000).toISOString();
    case '1hour':
      return new Date(now.getTime() + 60 * 60 * 1000).toISOString();
    case '2hours':
      return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
    case 'tomorrow': {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow.toISOString();
    }
    case 'nextweek': {
      const nextMonday = new Date(now);
      const dayOfWeek = nextMonday.getDay();
      const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
      nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
      nextMonday.setHours(9, 0, 0, 0);
      return nextMonday.toISOString();
    }
    default:
      return new Date(now.getTime() + 30 * 60 * 1000).toISOString();
  }
};

const formatSnoozeUntil = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (date.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow at ${timeStr}`;
  }

  const dayStr = date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  return `${dayStr} at ${timeStr}`;
};

interface PauseNotificationsMenuProps {
  onToggleSubmenu: (show: boolean) => void;
}

export const PauseNotificationsMenuItem = ({ onToggleSubmenu }: PauseNotificationsMenuProps) => {
  const snoozeUntil = useSelector(snoozeUntilSelector);
  const muteChat = useSelector(snoozeMuteChatSelector);
  const muteCalls = useSelector(snoozeMuteCallsSelector);
  const audio = useSelector(userAudioSelector);
  const dispatch = useDispatch();
  const { userId } = useContext(UserInfoContext);

  const isSnoozed = useMemo(() => {
    if (!snoozeUntil) return false;
    return new Date(snoozeUntil) > new Date();
  }, [snoozeUntil]);

  const isAudioMuted = audio === false;
  const isActive = isSnoozed || isAudioMuted;

  const statusDescription = useMemo(() => {
    if (isAudioMuted) return 'Sounds are muted';
    if (!isSnoozed || !snoozeUntil) return null;
    const types = [muteChat && 'Chat', muteCalls && 'Calls'].filter(Boolean).join(' and ');
    return `${types} Sounds are paused til ${formatSnoozeUntil(snoozeUntil)}`;
  }, [isSnoozed, isAudioMuted, snoozeUntil, muteChat, muteCalls]);

  const handleClick = (e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    if (!isAudioMuted) onToggleSubmenu(true);
  };

  return (
    <MenuItemContainer $multiline={isActive} onClick={handleClick} style={isAudioMuted ? { cursor: 'default' } : undefined}>
      <Icon>
        <Volume2 size={18} />
      </Icon>
      <ItemContent>
        <FlexAlignCenter style={{ justifyContent: 'space-between', width: '100%' }}>
          <Text whiteSpace="nowrap" fontSize="14px">Mute Sounds</Text>
          <FlexAlignCenter style={{ gap: '6px' }}>
            {isActive && <Text fontSize="14px" fontWeight={600} color={colors.green}>On</Text>}
            {!isAudioMuted && <ChevronRight size={16} color={colors.midGray} />}
          </FlexAlignCenter>
        </FlexAlignCenter>
        {isActive && statusDescription && (
          <Text fontSize="12px" color="#505050" style={{ marginTop: '4px', whiteSpace: 'normal', lineHeight: '18px' }}>
            {statusDescription}
          </Text>
        )}
        {isSnoozed && !isAudioMuted && (
          <SpanLink
            style={{ fontSize: '12px', marginTop: '4px' }}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(resumeNotifications(userId));
            }}
          >
            Resume sounds
          </SpanLink>
        )}
      </ItemContent>
    </MenuItemContainer>
  );
};

interface PauseNotificationsSubmenuProps {
  onBack: () => void;
}

export const PauseNotificationsSubmenu = ({ onBack }: PauseNotificationsSubmenuProps) => {
  const dispatch = useDispatch();
  const { userId } = useContext(UserInfoContext);
  const snoozeUntil = useSelector(snoozeUntilSelector);
  const storedMuteChat = useSelector(snoozeMuteChatSelector);
  const storedMuteCalls = useSelector(snoozeMuteCallsSelector);

  const isActive = useMemo(() => {
    if (!snoozeUntil) return false;
    return new Date(snoozeUntil) > new Date();
  }, [snoozeUntil]);

  const [muteChat, setMuteChat] = useState(false);
  const [muteCalls, setMuteCalls] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTime, setCustomTime] = useState('');
  const [customError, setCustomError] = useState('');
  const minimumCustomTime = formatDateForDateTimeInput(destructureDate(getMinimumCustomSnoozeTime()));

  const statusDescription = useMemo(() => {
    if (!isActive || !snoozeUntil) return null;
    const types = [storedMuteChat && 'Chat', storedMuteCalls && 'Calls'].filter(Boolean).join(' and ');
    return `${types} Sounds are paused til ${formatSnoozeUntil(snoozeUntil)}`;
  }, [isActive, snoozeUntil, storedMuteChat, storedMuteCalls]);

  const handlePause = (option: string) => {
    if (!muteChat && !muteCalls) return;
    if (option === 'custom') {
      setShowCustomInput(true);
      setCustomError('');
      return;
    }
    dispatch(pauseNotifications({
      userId,
      snoozeUntil: getSnoozeUntil(option),
      muteChat,
      muteCalls,
    }));
    onBack();
  };

  const handleCustomSubmit = () => {
    if (!customTime) return;
    const selected = new Date(customTime);
    if (selected < getMinimumCustomSnoozeTime()) {
      setCustomError('Please select a time at least 1 minute from now');
      return;
    }
    dispatch(pauseNotifications({
      userId,
      snoozeUntil: selected.toISOString(),
      muteChat,
      muteCalls,
    }));
    onBack();
  };

  const handleResume = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(resumeNotifications(userId));
    onBack();
  };

  const durationOptions = [
    { label: 'For 30 mins', value: '30min' },
    { label: 'For 1 Hour', value: '1hour' },
    { label: 'For 2 Hours', value: '2hours' },
    { label: 'Until Tomorrow', value: 'tomorrow' },
    { label: 'Until Next Week', value: 'nextweek' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <SubmenuContainer>
      <BackHeader onClick={(e) => { e.nativeEvent.stopImmediatePropagation(); onBack(); }}>
        <ChevronLeft size={18} />
        <Text fontSize="14px" fontWeight={600}>Mute Sounds</Text>
      </BackHeader>

      {isActive && statusDescription && (
        <StatusSection>
          <Text fontSize="12px" color="#505050" style={{ lineHeight: '18px' }}>
            {statusDescription}
          </Text>
          <SpanLink style={{ fontSize: '12px' }} onClick={handleResume}>
            Resume sounds
          </SpanLink>
        </StatusSection>
      )}

      <Divider />

      <Section>
        <SectionTitle>
          <Text fontSize="12px" fontWeight={600} color={colors.orange}>
            Sound types
          </Text>
        </SectionTitle>
        <CheckboxRow onClick={() => setMuteChat(v => !v)}>
          <CheckboxWrap><Checkbox checked={muteChat} onChange={() => {}} /></CheckboxWrap>
          <Text fontSize="14px">Mute Incoming Messages</Text>
        </CheckboxRow>
        <CheckboxRow onClick={() => setMuteCalls(v => !v)}>
          <CheckboxWrap><Checkbox checked={muteCalls} onChange={() => {}} /></CheckboxWrap>
          <Text fontSize="14px">Mute Incoming Calls</Text>
        </CheckboxRow>
      </Section>

      <Divider />

      <Section>
        <SectionTitle>
          <Text fontSize="12px" fontWeight={600} color={colors.orange}>
            {isActive ? 'Adjust time...' : 'Pause sounds for...'}
          </Text>
        </SectionTitle>
        {durationOptions.map(({ label, value }) => (
          <DurationRow
            key={value}
            onClick={(e) => { e.nativeEvent.stopImmediatePropagation(); handlePause(value); }}
            $disabled={!muteChat && !muteCalls}
          >
            <Text fontSize="14px">{label}</Text>
          </DurationRow>
        ))}
        {showCustomInput && (
          <CustomTimeSection>
            <DateTimeInput
              $compact
              type="datetime-local"
              value={customTime}
              min={minimumCustomTime}
              onChange={(e) => { setCustomTime(e.target.value); setCustomError(''); }}
              onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
            />
            <CustomTimeActions>
              <CustomTimeButton onClick={(e) => { e.nativeEvent.stopImmediatePropagation(); handleCustomSubmit(); }}>
                Confirm
              </CustomTimeButton>
              <CustomTimeCancelButton onClick={(e) => { e.nativeEvent.stopImmediatePropagation(); setShowCustomInput(false); setCustomError(''); }}>
                Cancel
              </CustomTimeCancelButton>
            </CustomTimeActions>
            {customError && <Text fontSize="11px" color={colors.red}>{customError}</Text>}
          </CustomTimeSection>
        )}
      </Section>
    </SubmenuContainer>
  );
};

const MenuItemContainer = styled.div<{ multiline?: boolean }>`
  display: flex;
  align-items: ${({ $multiline }) => $multiline ? 'flex-start' : 'center'};
  cursor: pointer;
  justify-content: flex-start;
  border-radius: 12px;
  padding: 5px 15px 5px 5px;
  &:hover {
    background-color: rgba(251, 248, 243, 1);
  }
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(251, 248, 243, 1);
  margin-right: 10px;
  flex-shrink: 0;
`;

const ItemContent = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
`;

const SubmenuContainer = styled(FlexColumn)`
  padding: 10px 0;
`;

const BackHeader = styled(FlexAlignCenter)`
  cursor: pointer;
  padding: 5px 20px 10px;
  gap: 8px;
  &:hover {
    opacity: 0.7;
  }
`;

const StatusSection = styled(FlexColumn)`
  padding: 0 24px 10px;
  gap: 6px;
`;

const Section = styled(FlexColumn)`
  padding: 0 24px;
`;

const SectionTitle = styled.div`
  padding: 10px 0 8px;
`;

const CheckboxRow = styled(FlexAlignCenter)`
  cursor: pointer;
  padding: 8px 0;
  gap: 14px;
`;

const CheckboxWrap = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const DurationRow = styled.div<{ disabled?: boolean }>`
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  padding: 8px 0;
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  &:hover {
    ${({ $disabled }) => !$disabled && 'opacity: 0.7;'}
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(150, 150, 150, 0.5);
  margin: 4px 4px;
`;

const CustomTimeSection = styled(FlexColumn)`
  gap: 8px;
  padding: 8px 0;
`;

const CustomTimeActions = styled(FlexAlignCenter)`
  gap: 8px;
`;

const CustomTimeButton = styled.button`
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  background: ${colors.orange};
  color: white;
  font-size: 12px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
`;

const CustomTimeCancelButton = styled.button`
  padding: 4px 12px;
  border: 1px solid ${colors.greyer};
  border-radius: 6px;
  background: white;
  color: ${colors.midGray};
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  &:hover {
    background: ${colors.sand};
  }
`;
